import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, Trophy, Radio, Users, ChevronRight, MapPin, Clock, 
  Menu, X, LogOut, User, Calendar, TrendingUp, Award, Target, 
  Activity, Zap, Star, Shield, Newspaper, ExternalLink, Globe, Search,
  ShoppingBag, GraduationCap, Bell, HelpCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import axios from 'axios';
import { API } from '@/App';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import GlobalSearch from '@/components/GlobalSearch';
import NotificationsDropdown from '@/components/NotificationsDropdown';
import LiveScoreTicker from '@/components/LiveScoreTicker';

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/f617a5a9-dda7-40dc-bbb9-de762658adaf/images/5d32d876981551b4c98ceec76bfdad1e26bc99093c09f2a5f2242b603b114d79.png";
const HERO_IMAGE_1 = "https://customer-assets.emergentagent.com/job_wicket2wicket/artifacts/h3wft36i_image.png";
const HERO_IMAGE_2 = "https://customer-assets.emergentagent.com/job_wicket2wicket/artifacts/4d71gc91_image.png";

export default function HomePage({ user, setUser }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchOpen, setSearchOpen] = useState(false);
  const [assignedMatches, setAssignedMatches] = useState([]);
  const [cricketNews, setCricketNews] = useState([]);
  const [newsLoading, setNewsLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/stats/public`);
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  // Fetch cricket news
  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await axios.get(`${API}/news/cricket`);
        setCricketNews(response.data.news || []);
      } catch (error) {
        console.error('Error fetching news:', error);
      } finally {
        setNewsLoading(false);
      }
    };
    fetchNews();
  }, []);

  // Fetch assigned matches for scorers
  useEffect(() => {
    const fetchAssignedMatches = async () => {
      if (user && (user.role === 'scorer' || user.role === 'admin')) {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${API}/matches/assigned`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setAssignedMatches(response.data);
        } catch (error) {
          console.error('Error fetching assigned matches:', error);
        }
      }
    };
    fetchAssignedMatches();
  }, [user]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) {}
    setUser(null);
  };

  const formatDateTime = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'MMM d • h:mm a');
    } catch {
      return dateStr;
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'MMM d');
    } catch {
      return dateStr;
    }
  };

  const getTimeAgo = (dateStr) => {
    try {
      return formatDistanceToNow(parseISO(dateStr), { addSuffix: true });
    } catch {
      return '';
    }
  };

  const getDashboardLink = () => {
    if (!user) return null;
    if (user.role === 'admin') return '/admin';
    if (user.role === 'scorer') return '/scorer';
    return null;
  };

  const getWinner = (match) => {
    if (match.innings?.length >= 2) {
      const team1Score = match.innings[0]?.total_runs || 0;
      const team2Score = match.innings[1]?.total_runs || 0;
      if (team2Score > team1Score) return match.team_b;
      if (team1Score > team2Score) return match.team_a;
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-slate-50 main-content-mobile">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-lg border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="CricORama" className="w-9 h-9 object-contain" />
              <span className="font-bold text-lg text-slate-900 font-display">CricORama</span>
            </Link>
            
            {/* User Guide Link */}
            <Link 
              to="/guide" 
              className="hidden sm:flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-teal-600 transition-colors ml-4"
              data-testid="nav-guide"
            >
              <HelpCircle className="w-4 h-4" />
              <span>Guide</span>
            </Link>

            <nav className="hidden md:flex items-center gap-4">
              <Link to="/leagues" className="text-sm font-medium text-slate-600 hover:text-blue-600">Leagues</Link>
              <Link to="/live" className="text-sm font-medium text-slate-600 hover:text-blue-600 flex items-center gap-1">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                Live
              </Link>
              
              {/* Quick Links - Merchandise & Lessons */}
              <a
                href="https://www.starsportsus.net/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-amber-600 hover:text-amber-700 flex items-center gap-1"
                data-testid="nav-merchandise"
              >
                <ShoppingBag className="w-4 h-4" />
                <span className="hidden lg:inline">Shop</span>
              </a>
              <Link 
                to="/lessons" 
                className="text-sm font-medium text-teal-600 hover:text-teal-700 flex items-center gap-1"
                data-testid="nav-lessons"
              >
                <GraduationCap className="w-4 h-4" />
                <span className="hidden lg:inline">Lessons</span>
              </Link>

              {getDashboardLink() && (
                <Link to={getDashboardLink()} className="text-sm font-medium text-slate-600 hover:text-blue-600">Dashboard</Link>
              )}

              {/* Live Score Ticker */}
              <LiveScoreTicker />
            </nav>

            <div className="flex items-center gap-2">
              {/* Search Button */}
              <button
                onClick={() => setSearchOpen(true)}
                className="p-2 rounded-full hover:bg-gray-100 text-gray-600 transition-colors"
                data-testid="home-search-btn"
              >
                <Search className="w-5 h-5" />
              </button>

              {/* Notifications Bell */}
              <NotificationsDropdown user={user} />

              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="gap-2" data-testid="user-menu">
                      <Avatar className="w-7 h-7">
                        <AvatarImage src={user.profile_image_url} />
                        <AvatarFallback className="bg-blue-600 text-white text-xs">
                          {user.name?.charAt(0)?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <span className="hidden sm:inline text-sm">{user.name?.split(' ')[0]}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <div className="px-2 py-1.5">
                      <p className="text-sm font-medium">{user.name}</p>
                      <p className="text-xs text-slate-500">{user.email}</p>
                    </div>
                    <DropdownMenuSeparator />
                    {getDashboardLink() && (
                      <DropdownMenuItem asChild>
                        <Link to={getDashboardLink()} className="cursor-pointer">
                          <User className="w-4 h-4 mr-2" /> Dashboard
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer">
                      <LogOut className="w-4 h-4 mr-2" /> Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link to="/login">
                  <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white rounded-full px-5">
                    Sign In
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-14 relative overflow-hidden min-h-[600px] md:min-h-[700px]">
        {/* Background with overlay */}
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${HERO_IMAGE_2})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center right'
          }}
        />
        {/* Stronger dark overlay for better text contrast */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F2828]/95 via-[#1A3D3D]/90 to-[#1A3D3D]/60"></div>
        
        {/* Animated particles effect */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-2 h-2 bg-[#6BA3A3] rounded-full animate-ping opacity-75"></div>
          <div className="absolute top-40 left-1/4 w-3 h-3 bg-[#8B1E3F] rounded-full animate-pulse"></div>
          <div className="absolute bottom-32 left-20 w-2 h-2 bg-[#C5D5D5] rounded-full animate-ping opacity-50" style={{animationDelay: '1s'}}></div>
        </div>
        
        <div className="relative z-10 px-4 py-16 md:py-24">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              {/* Left side - Text */}
              <div className="text-center lg:text-left">
                <Badge className="bg-[#8B1E3F] text-white border-none mb-4 text-sm px-4 py-1">
                  🏏 Season 2026 Live Now
                </Badge>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-4">
                  <span className="text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">Your Cricket</span><br />
                  <span className="text-[#FFD700] drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">Journey Starts Here</span>
                </h1>
                <p className="text-white text-lg max-w-md mb-8 mx-auto lg:mx-0 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
                  Track live scores, manage leagues, and celebrate every wicket with CricORama.
                </p>
                <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
                  <Link to="/leagues">
                    <Button size="lg" className="bg-[#8B1E3F] hover:bg-[#6D1830] text-white rounded-full px-8 font-semibold shadow-xl shadow-[#8B1E3F]/50 h-14 text-lg border-2 border-[#A52A4A]" data-testid="explore-leagues-btn">
                      Explore Leagues <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/live">
                    <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#1E4545] rounded-full px-8 font-semibold h-14 text-lg backdrop-blur-sm shadow-lg">
                      <Radio className="w-5 h-5 mr-2" /> Live Scores
                    </Button>
                  </Link>
                </div>
              </div>

              {/* Right side - Stats Grid with Player Image */}
              <div className="relative">
                {/* Floating player image */}
                <div className="hidden lg:block absolute -right-20 -top-10 w-80 h-96 z-20">
                  <img 
                    src={HERO_IMAGE_1} 
                    alt="Cricket Player" 
                    className="w-full h-full object-contain drop-shadow-2xl animate-float"
                    style={{animation: 'float 3s ease-in-out infinite'}}
                  />
                </div>
                
                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-4 max-w-sm mx-auto lg:mx-0">
                  <div className="bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/20 hover:bg-white/15 transition-all hover:scale-105 hover:shadow-xl">
                    <Trophy className="w-7 h-7 text-[#6BA3A3] mb-2" />
                    <div className="font-stats text-4xl text-white">{stats?.leagues_count || 0}</div>
                    <div className="text-sm text-white/70">Active Leagues</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/20 hover:bg-white/15 transition-all hover:scale-105 hover:shadow-xl">
                    <Shield className="w-7 h-7 text-[#6BA3A3] mb-2" />
                    <div className="font-stats text-4xl text-white">{stats?.teams_count || 0}</div>
                    <div className="text-sm text-white/70">Teams</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/20 hover:bg-white/15 transition-all hover:scale-105 hover:shadow-xl">
                    <Users className="w-7 h-7 text-[#6BA3A3] mb-2" />
                    <div className="font-stats text-4xl text-white">{stats?.players_count || 0}</div>
                    <div className="text-sm text-white/70">Players</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/20 hover:bg-white/15 transition-all hover:scale-105 hover:shadow-xl">
                    <Target className="w-7 h-7 text-[#6BA3A3] mb-2" />
                    <div className="font-stats text-4xl text-white">{stats?.matches_count || 0}</div>
                    <div className="text-sm text-white/70">Matches</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* My Assigned Matches - For Scorers */}
      {user && (user.role === 'scorer' || user.role === 'admin') && assignedMatches.length > 0 && (
        <section className="py-6 px-4 bg-gradient-to-r from-[#2D5A5A] to-[#3D6A6A]">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Radio className="w-5 h-5" />
                My Assigned Matches
                <Badge className="bg-white/20 text-white border-none ml-2">{assignedMatches.length}</Badge>
              </h2>
              <Link to="/scorer" className="text-white/80 text-sm hover:text-white flex items-center gap-1">
                View All <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {assignedMatches.slice(0, 3).map((match) => (
                <Link 
                  key={match.match_id} 
                  to={match.status === 'live' ? `/scorer/match/${match.match_id}/scoring` : '/scorer'}
                >
                  <Card className="bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/20 transition-all cursor-pointer">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <Badge className={`${
                          match.status === 'live' ? 'bg-red-500' : 
                          match.status === 'scheduled' ? 'bg-[#5D8A8A]' : 'bg-green-500'
                        } text-white border-none`}>
                          {match.status === 'live' ? '🔴 LIVE' : match.status === 'scheduled' ? 'UPCOMING' : 'COMPLETED'}
                        </Badge>
                        {match.status === 'live' && (
                          <span className="text-xs text-white/80">Tap to score</span>
                        )}
                      </div>
                      <div className="flex items-center justify-between text-white">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center text-sm font-bold">
                            {match.team_a?.short_name?.charAt(0) || 'A'}
                          </div>
                          <span className="font-medium">{match.team_a?.short_name || 'Team A'}</span>
                        </div>
                        <span className="text-xs font-bold text-white/60">VS</span>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{match.team_b?.short_name || 'Team B'}</span>
                          <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center text-sm font-bold">
                            {match.team_b?.short_name?.charAt(0) || 'B'}
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center justify-between text-xs text-white/70">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {match.ground?.split(' ')[0]}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDateTime(match.match_date_time)}
                        </span>
                      </div>
                      {match.status === 'scheduled' && (
                        <Button className="w-full mt-3 bg-white text-orange-600 hover:bg-white/90 h-9 text-sm font-semibold">
                          Start Match
                        </Button>
                      )}
                      {match.status === 'live' && (
                        <Button className="w-full mt-3 bg-red-500 text-white hover:bg-red-600 h-9 text-sm font-semibold">
                          Continue Scoring →
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Quick Stats Bar */}
      <section className="bg-white border-b border-slate-200 py-4 px-4 overflow-x-auto">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-6 min-w-max">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium">{stats?.live_matches_count || 0} Live</span>
            </div>
            <div className="h-4 w-px bg-slate-200"></div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-blue-600" />
              <span className="text-sm text-slate-600">{stats?.upcoming_matches?.length || 0} Upcoming</span>
            </div>
            <div className="h-4 w-px bg-slate-200"></div>
            <div className="flex items-center gap-2">
              <Award className="w-4 h-4 text-orange-500" />
              <span className="text-sm text-slate-600">{stats?.completed_count || 0} Completed</span>
            </div>
            <div className="h-4 w-px bg-slate-200"></div>
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-purple-500" />
              <span className="text-sm text-slate-600">Season in Progress</span>
            </div>
          </div>
        </div>
      </section>

      {/* Player Stats Banner */}
      <section className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <Link to="/stats">
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#2D5A5A] via-[#3D6A6A] to-[#5D8A8A] p-6 md:p-8 shadow-xl hover:shadow-2xl transition-all transform hover:scale-[1.01] cursor-pointer" data-testid="stats-banner">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl transform translate-x-1/3 -translate-y-1/3"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#8B1E3F]/20 rounded-full blur-3xl transform -translate-x-1/3 translate-y-1/3"></div>
              
              <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="text-center md:text-left">
                  <div className="flex items-center gap-3 justify-center md:justify-start mb-2">
                    <Activity className="w-8 h-8 text-[#6BA3A3]" />
                    <h2 className="text-2xl md:text-3xl font-black text-white">Player Statistics</h2>
                  </div>
                  <p className="text-white/80 max-w-md">
                    Explore leaderboards, player rankings, Orange Cap, Purple Cap winners and comprehensive cricket statistics
                  </p>
                </div>
                
                <div className="flex flex-wrap items-center gap-4">
                  <div className="text-center px-4 py-2 bg-white/10 rounded-xl backdrop-blur">
                    <div className="text-2xl font-black text-[#6BA3A3]">🏆</div>
                    <div className="text-xs text-white/70">Orange Cap</div>
                  </div>
                  <div className="text-center px-4 py-2 bg-white/10 rounded-xl backdrop-blur">
                    <div className="text-2xl font-black text-[#8B1E3F]">🎯</div>
                    <div className="text-xs text-white/70">Purple Cap</div>
                  </div>
                  <Button className="bg-white text-[#2D5A5A] hover:bg-white/90 rounded-full px-6 font-semibold shadow-lg">
                    View Stats <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* Shop & Learn Modules */}
      <section className="py-8 px-4 bg-[#F5F8F8]">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Cricket Merchandise */}
            <a 
              href="https://www.starsportsus.net/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group"
            >
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#8B1E3F] to-[#6D1830] p-8 h-full min-h-[280px] shadow-xl hover:shadow-2xl transition-all transform hover:scale-[1.02]">
                <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full blur-3xl transform translate-x-10 -translate-y-10"></div>
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-black/10 rounded-full blur-2xl transform -translate-x-5 translate-y-5"></div>
                
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center text-3xl">
                      🛒
                    </div>
                    <Badge className="bg-white/20 text-white border-none">Shop Now</Badge>
                  </div>
                  
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                    Cricket Merchandise
                  </h3>
                  <p className="text-white/80 mb-6">
                    Get premium cricket gear, bats, balls, protective equipment, and apparel from Star Sports US.
                  </p>
                  
                  <div className="flex items-center gap-2 text-white font-semibold group-hover:gap-4 transition-all">
                    <span>Visit Store</span>
                    <ExternalLink className="w-5 h-5" />
                  </div>
                </div>
              </div>
            </a>

            {/* Online Cricket Lessons */}
            <Link to="/lessons" className="group">
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#2D5A5A] to-[#1E4545] p-8 h-full min-h-[280px] shadow-xl hover:shadow-2xl transition-all transform hover:scale-[1.02]">
                <div className="absolute top-0 right-0 w-40 h-40 bg-[#FFD700]/10 rounded-full blur-3xl transform translate-x-10 -translate-y-10"></div>
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-black/10 rounded-full blur-2xl transform -translate-x-5 translate-y-5"></div>
                
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center text-3xl">
                      🎓
                    </div>
                    <Badge className="bg-[#FFD700]/20 text-[#FFD700] border-none">Coming Soon</Badge>
                  </div>
                  
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                    Online Cricket Lessons
                  </h3>
                  <p className="text-white/80 mb-6">
                    Learn batting, bowling, and fielding techniques from professional coaches. All skill levels welcome.
                  </p>
                  
                  <div className="flex items-center gap-2 text-[#FFD700] font-semibold group-hover:gap-4 transition-all">
                    <span>Learn More</span>
                    <ChevronRight className="w-5 h-5" />
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Cricket News Section */}
      <section className="py-12 px-4 bg-gradient-to-b from-white to-[#F5F8F8]">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#8B1E3F] to-[#6D1830] flex items-center justify-center shadow-lg shadow-[#8B1E3F]/30">
                <Newspaper className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900">Cricket News</h2>
                <p className="text-sm text-slate-500">Latest updates from the cricket world</p>
              </div>
            </div>
            <Badge className="bg-[#2D5A5A]/10 text-[#2D5A5A] border-none hidden sm:flex items-center gap-1">
              <Globe className="w-4 h-4" />
              Live Updates
            </Badge>
          </div>
          
          {newsLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-10 h-10 border-4 border-[#2D5A5A] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : cricketNews.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {cricketNews.slice(0, 6).map((news, idx) => (
                <a 
                  key={idx} 
                  href={news.link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="group"
                  data-testid={`news-card-${idx}`}
                >
                  <Card className="h-full border-0 shadow-md hover:shadow-xl transition-all bg-white overflow-hidden group-hover:-translate-y-1 duration-300">
                    {/* News Image */}
                    <div className="relative h-48 overflow-hidden bg-slate-100">
                      <img 
                        src={news.image} 
                        alt={news.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => {
                          e.target.src = 'https://images.unsplash.com/photo-1531415074968-036ba1b575da?w=400&h=250&fit=crop';
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                      <Badge className="absolute top-3 left-3 bg-white/90 text-[#2D5A5A] border-none text-xs font-medium backdrop-blur-sm">
                        {news.source?.replace('RSS', '').replace(' - Cricket', '').trim() || 'Cricket'}
                      </Badge>
                      <div className="absolute bottom-3 right-3 w-8 h-8 rounded-full bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <ExternalLink className="w-4 h-4 text-[#8B1E3F]" />
                      </div>
                    </div>
                    
                    <CardContent className="p-5">
                      <h3 className="font-semibold text-slate-800 mb-2 line-clamp-2 group-hover:text-[#2D5A5A] transition-colors leading-snug">
                        {news.title}
                      </h3>
                      <p className="text-sm text-slate-500 line-clamp-2 mb-4">
                        {news.summary}
                      </p>
                      <div className="flex items-center justify-between">
                        {news.published && (
                          <p className="text-xs text-slate-400 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDistanceToNow(new Date(news.published), { addSuffix: true })}
                          </p>
                        )}
                        <span className="text-xs font-medium text-[#8B1E3F] group-hover:underline">
                          Read more →
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </a>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-slate-50 rounded-2xl">
              <Newspaper className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500 text-lg">No news available at the moment</p>
              <p className="text-slate-400 text-sm mt-1">Check back later for the latest cricket updates</p>
            </div>
          )}
        </div>
      </section>

      {/* Live Matches */}
      {stats?.live_matches?.length > 0 && (
        <section className="py-6 px-4 bg-gradient-to-r from-[#F5F8F8] to-[#E8F0F0]">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 mb-4">
              <div className="live-dot"></div>
              <h2 className="text-xl font-bold text-slate-900">Live Now</h2>
              <Badge className="badge-live ml-2">{stats.live_matches.length}</Badge>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {stats.live_matches.map((match) => (
                <Link key={match.match_id} to={`/matches/${match.match_id}`}>
                  <Card className="card-base card-live cursor-pointer hover:shadow-lg transition-all">
                    <CardContent className="p-4">
                      <Badge className="badge-live mb-3">LIVE</Badge>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl team-logo-placeholder text-sm">
                            {match.team_a?.short_name?.charAt(0) || 'A'}
                          </div>
                          <span className="font-semibold">{match.team_a?.short_name || match.team_a?.name}</span>
                        </div>
                        {match.innings?.[0] && (
                          <span className="font-stats text-xl">{match.innings[0].total_runs}/{match.innings[0].total_wickets}</span>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl team-logo-placeholder bg-[#5D8A8A] text-sm">
                            {match.team_b?.short_name?.charAt(0) || 'B'}
                          </div>
                          <span className="font-semibold">{match.team_b?.short_name || match.team_b?.name}</span>
                        </div>
                        {match.innings?.[1] && (
                          <span className="font-stats text-xl">{match.innings[1].total_runs}/{match.innings[1].total_wickets}</span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Main Content Grid */}
      <section className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left Column - Upcoming & Results */}
            <div className="lg:col-span-2 space-y-6">
              {/* Upcoming Matches */}
              <Card className="card-base">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-lg font-bold flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-blue-600" />
                    Upcoming Matches
                  </CardTitle>
                  <Link to="/leagues" className="text-blue-600 text-sm font-medium flex items-center gap-1">
                    View All <ChevronRight className="w-4 h-4" />
                  </Link>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  {loading ? (
                    <div className="space-y-3">
                      {[1, 2, 3].map(i => <div key={i} className="skeleton h-20 rounded-xl"></div>)}
                    </div>
                  ) : stats?.upcoming_matches?.length > 0 ? (
                    <div className="space-y-3">
                      {stats.upcoming_matches.map((match) => (
                        <Link key={match.match_id} to={`/matches/${match.match_id}`}>
                          <div className="p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors border border-slate-100">
                            <div className="flex items-center justify-between mb-2">
                              <Badge variant="outline" className="text-xs">{match.league?.name || 'League'}</Badge>
                              <span className="text-xs text-slate-500">{formatDateTime(match.match_date_time)}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-lg team-logo-placeholder text-xs">
                                  {match.team_a?.short_name?.charAt(0)}
                                </div>
                                <span className="font-medium text-sm">{match.team_a?.short_name}</span>
                              </div>
                              <span className="text-xs font-bold text-slate-400">VS</span>
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-sm">{match.team_b?.short_name}</span>
                                <div className="w-8 h-8 rounded-lg team-logo-placeholder bg-orange-500 text-xs">
                                  {match.team_b?.short_name?.charAt(0)}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                              <MapPin className="w-3 h-3" />
                              <span>{match.ground}</span>
                              <span>•</span>
                              <span>{match.overs} overs</span>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <Calendar className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p>No upcoming matches</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Recent Results */}
              <Card className="card-base">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-lg font-bold flex items-center gap-2">
                    <Award className="w-5 h-5 text-orange-500" />
                    Recent Results
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  {loading ? (
                    <div className="space-y-3">
                      {[1, 2].map(i => <div key={i} className="skeleton h-24 rounded-xl"></div>)}
                    </div>
                  ) : stats?.recent_results?.length > 0 ? (
                    <div className="space-y-3">
                      {stats.recent_results.map((match) => {
                        const winner = getWinner(match);
                        return (
                          <Link key={match.match_id} to={`/matches/${match.match_id}`}>
                            <div className="p-4 rounded-xl bg-gradient-to-r from-slate-50 to-white border border-slate-100 hover:shadow-md transition-all">
                              <div className="flex items-center justify-between mb-3">
                                <Badge className="badge-completed text-xs">COMPLETED</Badge>
                                <span className="text-xs text-slate-500">{getTimeAgo(match.updated_at)}</span>
                              </div>
                              <div className="grid grid-cols-3 items-center gap-2">
                                <div className={`text-center p-2 rounded-lg ${winner?.short_name === match.team_a?.short_name ? 'bg-green-50' : ''}`}>
                                  <div className="w-10 h-10 rounded-xl team-logo-placeholder text-sm mx-auto mb-1">
                                    {match.team_a?.short_name?.charAt(0)}
                                  </div>
                                  <p className="font-semibold text-sm">{match.team_a?.short_name}</p>
                                  <p className="font-stats text-lg">{match.innings?.[0]?.total_runs || 0}/{match.innings?.[0]?.total_wickets || 0}</p>
                                  <p className="text-xs text-slate-500">({match.innings?.[0]?.total_overs?.toFixed(1) || 0})</p>
                                </div>
                                <div className="text-center">
                                  <span className="text-xs font-bold text-slate-400">VS</span>
                                </div>
                                <div className={`text-center p-2 rounded-lg ${winner?.short_name === match.team_b?.short_name ? 'bg-green-50' : ''}`}>
                                  <div className="w-10 h-10 rounded-xl team-logo-placeholder bg-orange-500 text-sm mx-auto mb-1">
                                    {match.team_b?.short_name?.charAt(0)}
                                  </div>
                                  <p className="font-semibold text-sm">{match.team_b?.short_name}</p>
                                  <p className="font-stats text-lg">{match.innings?.[1]?.total_runs || 0}/{match.innings?.[1]?.total_wickets || 0}</p>
                                  <p className="text-xs text-slate-500">({match.innings?.[1]?.total_overs?.toFixed(1) || 0})</p>
                                </div>
                              </div>
                              {winner && (
                                <div className="mt-3 text-center">
                                  <Badge className="bg-green-100 text-green-700 border-green-200">
                                    <Star className="w-3 h-3 mr-1" />
                                    {winner.name} Won
                                  </Badge>
                                </div>
                              )}
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <Award className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p>No completed matches yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Leagues & Teams */}
            <div className="space-y-6">
              {/* Leagues */}
              <Card className="card-base">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-lg font-bold flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-yellow-500" />
                    Leagues
                  </CardTitle>
                  <Link to="/leagues" className="text-blue-600 text-sm font-medium">View All</Link>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  {loading ? (
                    <div className="space-y-2">
                      {[1, 2, 3].map(i => <div key={i} className="skeleton h-16 rounded-xl"></div>)}
                    </div>
                  ) : stats?.leagues?.length > 0 ? (
                    <div className="space-y-2">
                      {stats.leagues.map((league) => (
                        <Link key={league.league_id} to={`/leagues/${league.league_id}`}>
                          <div className="p-3 rounded-xl hover:bg-slate-50 transition-colors border border-transparent hover:border-slate-100">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold">
                                  {league.name?.charAt(0)}
                                </div>
                                <div>
                                  <p className="font-semibold text-sm">{league.name}</p>
                                  <p className="text-xs text-slate-500">{league.location}</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-xs font-medium">{league.team_count} teams</p>
                                <p className="text-xs text-slate-500">{league.match_count} matches</p>
                              </div>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-slate-500">
                      <Trophy className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No leagues yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Featured Teams */}
              <Card className="card-base">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-lg font-bold flex items-center gap-2">
                    <Shield className="w-5 h-5 text-blue-500" />
                    Featured Teams
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  {loading ? (
                    <div className="grid grid-cols-2 gap-2">
                      {[1, 2, 3, 4].map(i => <div key={i} className="skeleton h-20 rounded-xl"></div>)}
                    </div>
                  ) : stats?.featured_teams?.length > 0 ? (
                    <div className="grid grid-cols-2 gap-2">
                      {stats.featured_teams.slice(0, 6).map((team) => (
                        <Link key={team.team_id} to={`/teams/${team.team_id}`}>
                          <div className="p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors text-center">
                            <div className="w-10 h-10 rounded-xl team-logo-placeholder text-sm mx-auto mb-2">
                              {team.short_name?.charAt(0) || team.name?.charAt(0)}
                            </div>
                            <p className="font-semibold text-xs truncate">{team.short_name || team.name}</p>
                            <p className="text-[10px] text-slate-500">{team.player_count} players</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-slate-500">
                      <Shield className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No teams yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Quick Actions for logged in users */}
              {user && (
                <Card className="card-base bg-gradient-to-br from-blue-600 to-blue-700 text-white">
                  <CardContent className="p-4">
                    <h3 className="font-bold mb-3 flex items-center gap-2">
                      <Zap className="w-5 h-5" />
                      Quick Actions
                    </h3>
                    <div className="space-y-2">
                      {user.role === 'admin' && (
                        <>
                          <Link to="/admin/leagues">
                            <Button variant="secondary" size="sm" className="w-full justify-start bg-white/20 hover:bg-white/30 text-white border-0">
                              <Trophy className="w-4 h-4 mr-2" /> Manage Leagues
                            </Button>
                          </Link>
                          <Link to="/admin/matches">
                            <Button variant="secondary" size="sm" className="w-full justify-start bg-white/20 hover:bg-white/30 text-white border-0">
                              <Calendar className="w-4 h-4 mr-2" /> Schedule Match
                            </Button>
                          </Link>
                        </>
                      )}
                      {user.role === 'scorer' && (
                        <Link to="/scorer">
                          <Button variant="secondary" size="sm" className="w-full justify-start bg-white/20 hover:bg-white/30 text-white border-0">
                            <Radio className="w-4 h-4 mr-2" /> My Matches
                          </Button>
                        </Link>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 relative overflow-hidden">
        {/* Background with player image */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900"></div>
        <div 
          className="absolute right-0 top-0 bottom-0 w-1/2 opacity-30"
          style={{
            backgroundImage: `url(${HERO_IMAGE_1})`,
            backgroundSize: 'contain',
            backgroundPosition: 'right center',
            backgroundRepeat: 'no-repeat'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/80 to-transparent"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <Badge className="bg-[#8B1E3F]/20 text-[#8B1E3F] border-[#8B1E3F]/30 mb-4">Features</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Everything You Need</h2>
            <p className="text-white/60 max-w-2xl mx-auto">Powerful tools to manage your cricket leagues like a pro</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="group text-center p-8 rounded-3xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-all hover:scale-105 hover:shadow-2xl hover:shadow-[#8B1E3F]/10">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#8B1E3F] to-[#6D1830] flex items-center justify-center mx-auto mb-6 shadow-lg shadow-[#8B1E3F]/30 group-hover:scale-110 transition-transform">
                <Trophy className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-bold text-white text-xl mb-3">League Management</h3>
              <p className="text-white/60">Create and manage multiple leagues, teams, and players with our intuitive dashboard</p>
            </div>
            <div className="group text-center p-8 rounded-3xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-all hover:scale-105 hover:shadow-2xl hover:shadow-[#2D5A5A]/10">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#2D5A5A] to-[#1E4545] flex items-center justify-center mx-auto mb-6 shadow-lg shadow-[#2D5A5A]/30 group-hover:scale-110 transition-transform">
                <Radio className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-bold text-white text-xl mb-3">Live Scoring</h3>
              <p className="text-white/60">Ball-by-ball scoring with instant updates, wickets, extras, and real-time statistics</p>
            </div>
            <div className="group text-center p-8 rounded-3xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-all hover:scale-105 hover:shadow-2xl hover:shadow-[#5D8A8A]/10">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#5D8A8A] to-[#4A7575] flex items-center justify-center mx-auto mb-6 shadow-lg shadow-[#5D8A8A]/30 group-hover:scale-110 transition-transform">
                <TrendingUp className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-bold text-white text-xl mb-3">Statistics</h3>
              <p className="text-white/60">Detailed player and team stats, leaderboards, and performance analytics</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 px-4 bg-[#1E4545]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="CricORama" className="w-8 h-8 object-contain" />
            <span className="font-semibold text-white font-display">CricORama</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-[#6BA3A3]">
            <Link to="/leagues" className="hover:text-white transition-colors">Leagues</Link>
            <Link to="/live" className="hover:text-white transition-colors">Live</Link>
            {!user && <Link to="/login" className="hover:text-white transition-colors">Sign In</Link>}
          </div>
          <p className="text-[#5D8A8A] text-sm">© 2026 CricORama</p>
        </div>
      </footer>
      {/* Global Search Modal */}
      {searchOpen && <GlobalSearch onClose={() => setSearchOpen(false)} />}
    </div>
  );
}
